// Lightweight helpers for element-mode seeking
export function clampToSeekable(el: HTMLMediaElement, t: number) {
  try {
    const r = el.seekable;
    if (r && r.length > 0) {
      const start = r.start(0);
      const end = r.end(r.length - 1);
      return Math.max(start, Math.min(end, t));
    }
  } catch {}
  return t;
}

export function waitSeekReady(
  el: HTMLMediaElement,
  target: number,
  opts?: { seekMs?: number; readyMs?: number }
) {
  const seekMs = opts?.seekMs ?? 4000;  // generous for slow devices
  const readyMs = opts?.readyMs ?? 2000;

  return new Promise<void>((resolve) => {
    const t = clampToSeekable(el, target);
    try {
      (el as any).fastSeek ? (el as any).fastSeek(t) : (el.currentTime = t);
    } catch { /* noop */ }

    let gotSeeked = false;
    let gotReady = false;
    let seekTO: any = null;
    let readyTO: any = null;

    const cleanup = () => {
      try { el.removeEventListener('seeked', onSeeked); } catch {}
      try { el.removeEventListener('timeupdate', onSeeked); } catch {}
      try { el.removeEventListener('canplay', onReady); } catch {}
      try { el.removeEventListener('playing', onReady); } catch {}
      if (seekTO) clearTimeout(seekTO);
      if (readyTO) clearTimeout(readyTO);
    };

    const finishIfDone = () => {
      if (gotSeeked && gotReady) {
        cleanup();
        resolve();
      }
    };

    const onSeeked = () => { gotSeeked = true; finishIfDone(); };
    const onReady = () => { gotReady = true; finishIfDone(); };

    // Fast path if already satisfied
    if (Math.abs((el.currentTime || 0) - t) < 0.002) gotSeeked = true;
    if ((el as any).readyState >= (window as any).HTMLMediaElement?.HAVE_FUTURE_DATA) gotReady = true;
    if (gotSeeked && gotReady) { resolve(); return; }

    try { el.addEventListener('seeked', onSeeked, { once: true } as any); } catch {}
    // Some browsers may skip `seeked` on paused media — use `timeupdate` as a backup
    try { el.addEventListener('timeupdate', onSeeked, { once: true } as any); } catch {}
    try { el.addEventListener('canplay', onReady, { once: true } as any); } catch {}
    try { el.addEventListener('playing', onReady, { once: true } as any); } catch {}

    seekTO = setTimeout(() => { gotSeeked = true; finishIfDone(); }, seekMs);
    readyTO = setTimeout(() => { gotReady = true; finishIfDone(); }, readyMs);
  });
}
